import { Utils } from "@ts/core/Utils";
import { api } from "../core/api";
import { Elements } from "../core/Elements";
import { svg } from "../core/svg";
import { yesNo } from "../core/YesNo";
import { AbstractSection } from "./AbstractSection";

const enum MoveDirection {
    Top = 1,
    Up = 2,
    Down = 3,
    Bottom = 4,
    Undefined = 5
}

export class EditBorder {

    private section: AbstractSection;
    private sectionElement: HTMLElement;

    public constructor(section: AbstractSection) {
        this.section = section;
        this.sectionElement = this.section.sectionElementRef;
        Elements.help.classList.remove('hidden');
        this.sectionElement.style.outline = 'var(--outline-disabled)';
    }

    public render(): void {
        this.close();

        this.sectionElement.classList.add( this.section.isPublic ? 'green-border' : 'red-border' );

        if (!this.section.isEditActive) {
            this.sectionElement.appendChild(
                this.borderElement('up-btn', 
                    'Flytta avsnittet ett steg nedåt, dvs efter avsnittet nedan',
                    'Flytta uppåt', 'up', () => {
                this.moveElement(this.sectionElement, MoveDirection.Up);
            }));

            this.sectionElement.appendChild(
                this.borderElement('down-btn', 
                    'Flytta avsnittet ett steg uppåt, dvs före avsnittet ovan',
                    'Flytta nedåt', 'down', () => {
                this.moveElement(this.sectionElement, MoveDirection.Down);
            }));


            this.sectionElement.appendChild(this.borderElement('edit-btn', 
                'Starta redigering av avsnittet',
                'Redigera avsnittet', 'edit', async () => {
                this.section.beginEdit();
            }));

            this.sectionElement.appendChild(
                this.borderElement('delete-btn', 
                    'Radera hela avsnittet. Du får en "Är du säker" fråga först.',
                    'Radera avsnittet', 'delete', async () => {
                if (await yesNo('Radera avsnitt', 'Är du säker på att du vill radera avsnittet?')) {
                    await this.section.delete();
                }
            }));
        }
        else {
            
            this.sectionElement.appendChild(
                this.borderElement('abort-btn', 
                    'Avsluta redigering utan att spara. Du får en chans att ångra dig',
                    'Avbryt redigering', 'cancel', async () => {
                if (await yesNo('Avbryt redigering', 'Är du säker på att du vill avbryta redigeringen utan att spara?')) {
                    this.sectionElement.classList.remove('red-border');
                    this.sectionElement.classList.remove('green-border');
                    this.section.endEdit();
                }
            }));

            this.sectionElement.appendChild(
                this.borderElement('save-btn', 
                    'Spara avsnittet och avsluta redigering',
                    'Spara avsnittet', 'save', async () => {
                this.section.save();
                this.sectionElement.classList.remove('red-border');
                this.sectionElement.classList.remove('green-border');
                this.section.endEdit();
            }));

            
            this.sectionElement.appendChild(
                this.borderElement('public-btn', 
                    'Avsnittet visas offentligt. Markerat här av att avsnittet har en grön ram.',
                    'Offentlig', 'public', async () => {
                this.sectionElement.classList.remove('red-border');
                this.sectionElement.classList.add('green-border');
                this.section.isPublic = true;
            }));

            this.sectionElement.appendChild(
                this.borderElement('private-btn', 
                    'Avsnittet visas inte offentligt. Markerat här av att avsnittet har en röd ram.',
                    'Privat', 'private', async () => {
                this.section.isPublic = false;
                this.sectionElement.classList.remove('green-border');
                this.sectionElement.classList.add('red-border');
            }));
            
            
        }
    }

    public close(): void {
        this.sectionElement.querySelector('.up-btn')?.remove();
        this.sectionElement.querySelector('.down-btn')?.remove();
        this.sectionElement.querySelector('.edit-btn')?.remove();
        this.sectionElement.querySelector('.save-btn')?.remove();
        this.sectionElement.querySelector('.abort-btn')?.remove();
        this.sectionElement.querySelector('.delete-btn')?.remove();
        this.sectionElement.querySelector('.public-btn')?.remove();
        this.sectionElement.querySelector('.private-btn')?.remove();
        this.sectionElement.removeEventListener('click', async () => { });

    }

    private moveElement(element: HTMLElement, direction: MoveDirection): void {

        switch (direction) {

            case MoveDirection.Up:
                let prev = element.previousSibling;
                if (prev) {
                    Elements.contentContainer.insertBefore(element, prev);
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    this.updatePositions();
                }
                break;

            case MoveDirection.Down:
                let next = element.nextSibling;
                if (next) {
                    next = next.nextSibling;
                    Elements.contentContainer.insertBefore(element, next);
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    this.updatePositions();
                }
                break;
        }
    }

    // Hjälpfunktion för att skapa knapparna i dom fyra hörnen av avsnittet
    private borderElement(cls: string, title: string, help:string, iconname: string, cb: () => void): HTMLButtonElement {
        const btn = document.createElement('button') as HTMLButtonElement;
        btn.className = 'outline-btn';
        btn.classList.add(cls);
        btn.title = title;
        btn.innerHTML = svg(iconname, cls);
        btn.addEventListener('click', (e: Event) => {
            e.preventDefault();
            cb();
        });
        btn.addEventListener('mouseover', (e: Event) => {
            e.preventDefault();
            Elements.help.innerHTML = help;
            Elements.help.style.top = `${btn.parentElement?.offsetTop}px`;
            Utils.forceRefresh(Elements.help);
        });
        return btn;
    }

    private updatePositions(): void {
        const sections = Elements.contentContainer.querySelectorAll('section') as NodeListOf<HTMLElement>;
        if (!sections) return;

        const positions: number[] = [];
        for (let pos = 0; pos < sections.length; pos++) {
            const section = sections[pos];
            positions.push(parseInt(section.id));
        }
        api<void>('setContentPositions', {
            'positions': positions
        });
    }
}

